import { RefObject, useEffect, useRef } from 'react';
import {
  ACTION_BAR_SELECTOR,
  swallowNextClick,
  TABLE_VIEW_ROOT_SELECTOR
} from 'hooks/useOutsideTapDismiss';

/**
 * Marks the footer buttons that open a comms panel (Hand History, Game Chat).
 * A pointer on one of them is never "outside": the button toggles the panel
 * itself, and closing on pointerdown only to re-open on the click that follows
 * would flicker.
 */
export const COMMS_PANEL_TRIGGER_ATTR = 'data-comms-panel-trigger';

/**
 * What MUI portals to <body>: tooltips, menus, selects, dialogs. A tap on a
 * dialog painted over the table is not a tap on the table.
 */
const PORTAL_SELECTOR =
  '[role="tooltip"], [role="dialog"], [role="menu"], [role="listbox"], .MuiPopover-root, .MuiPopper-root, .MuiModal-root';

/**
 * Light dismiss for the comms panels.
 *
 * A pointer that goes down anywhere outside the open panel closes it, exactly
 * as the header's × does (Jason, 2026-09-23). `pointerdown` in the capture
 * phase, so the panel is gone before the felt's own handlers run and so touch,
 * pen and mouse arrive as one event. Only an open panel listens: a parked panel
 * on another table (useTableScopedOpen) never closes over a swipe it cannot see.
 *
 * Two rules carried over from useOutsideTapDismiss, which the panel this
 * replaced used: a pointer in ANOTHER grid cell's table view belongs to that
 * table, not this panel; and the tap that closes the panel over the poker action
 * bar is consumed, so it cannot also commit FOLD / CALL.
 */
/**
 * Consume the click of the dismissing tap only once that tap completes inside the
 * action bar. A press that is dragged off the bar or cancelled produces no click
 * of its own, so nothing is armed that could eat the player's next deliberate
 * action.
 */
const swallowClickOfThisTap = (pointerId: number) => {
  const finish = (event: PointerEvent) => {
    if (event.pointerId !== pointerId) return;
    document.removeEventListener('pointerup', finish, true);
    document.removeEventListener('pointercancel', finish, true);
    const target = event.target instanceof Element ? event.target : null;
    if (event.type === 'pointerup' && target?.closest(ACTION_BAR_SELECTOR)) {
      swallowNextClick(ACTION_BAR_SELECTOR);
    }
  };
  document.addEventListener('pointerup', finish, true);
  document.addEventListener('pointercancel', finish, true);
};

export const useDismissOnOutsidePointer = (
  panelRef: RefObject<HTMLElement>,
  active: boolean,
  onDismiss: () => void
) => {
  // The latest callback without re-subscribing when its identity changes.
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEffect(() => {
    if (!active) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (panelRef.current?.contains(target)) return;
      const element = target instanceof Element ? target : target.parentElement;
      if (element?.closest(`[${COMMS_PANEL_TRIGGER_ATTR}], ${PORTAL_SELECTOR}`)) return;
      const ownView = panelRef.current?.closest(TABLE_VIEW_ROOT_SELECTOR);
      const targetView = element?.closest(TABLE_VIEW_ROOT_SELECTOR);
      if (ownView && targetView && targetView !== ownView) return;
      if (element?.closest(ACTION_BAR_SELECTOR)) swallowClickOfThisTap(event.pointerId);
      onDismissRef.current();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [active, panelRef]);
};

export default useDismissOnOutsidePointer;
